import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { usePostsStore } from './postsStore';
import { toast } from '@/utils/toast';
import { devLog } from '@/utils/logger';

/**
 * Bulk Import Store
 *
 * Manages background bulk import jobs that continue when the dialog is closed.
 * Shows a minimized progress indicator in the bottom-left corner.
 *
 * UNLIKE Canva import store, this is SSE-driven (no polling).
 * SSE events from SSEContext update this store directly.
 */

interface BulkImportProgressEvent {
  jobId: number;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  processedRows: number;
  totalRows: number;
  successCount: number;
  failureCount: number;
  percentComplete: number;
  fileName?: string;
  error?: string;
}

interface BulkImportState {
  // Active background import
  activeImport: {
    jobId: number;
    status: BulkImportProgressEvent | null;
    requestReopen: boolean;
  } | null;

  // Actions
  startBackgroundImport: (jobId: number) => void;
  clearBackgroundImport: () => void;
  updateProgress: (data: BulkImportProgressEvent) => void;
  reopenModal: () => void;
}

export const useBulkImportStore = create<BulkImportState>()(
  devtools(
    (set, get) => ({
      activeImport: null,

      /**
       * Start tracking an import in the background
       * Called when user closes dialog during active import
       * No polling needed - SSE events will update progress
       */
      startBackgroundImport: (jobId: number) => {
        set({ activeImport: { jobId, status: null, requestReopen: false } });
        devLog.info('Bulk import moved to background', { jobId });
      },

      /**
       * Clear background import (user dismissed it)
       */
      clearBackgroundImport: () => {
        set({ activeImport: null });
      },

      /**
       * Update progress from SSE event
       * Called by SSEContext event handlers
       */
      updateProgress: (data: BulkImportProgressEvent) => {
        const { activeImport } = get();

        // Only update if this event is for our tracked import
        if (activeImport && activeImport.jobId === data.jobId) {
          set({
            activeImport: {
              ...activeImport,
              status: data,
            },
          });

          // Check if complete
          if (
            data.status === 'COMPLETED' ||
            data.status === 'FAILED' ||
            data.status === 'CANCELLED'
          ) {
            // Show toast notification
            if (data.status === 'COMPLETED') {
              toast.success(
                `Import complete: ${data.successCount}/${data.totalRows} rows imported`
              );
              // Refresh posts list
              usePostsStore.getState().fetchPosts();
              usePostsStore.getState().fetchStats();
            } else if (data.status === 'FAILED') {
              toast.error('Bulk import failed');
            } else {
              toast.error('Bulk import was cancelled');
            }

            // Clear after delay to show final status
            setTimeout(() => {
              set({ activeImport: null });
            }, 5000);
          }
        }
      },

      /**
       * Reopen the modal (called when user clicks minimized indicator)
       */
      reopenModal: () => {
        const { activeImport } = get();
        if (activeImport) {
          set({
            activeImport: {
              ...activeImport,
              requestReopen: true,
            },
          });
        }
      },
    }),
    {
      name: 'BulkImportStore',
      enabled: import.meta.env.DEV,
    }
  )
);

// Export the progress event type for SSE handlers
export type { BulkImportProgressEvent };
